import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://phennx.io';
const DEFAULT_TITLE = 'Phennx - Clinical Research Operations, In One Command Center';
const DEFAULT_DESCRIPTION =
  'Phennx helps research sites, sponsors, and CROs coordinate studies, visits, documents, monitoring, payments, and audit-ready workflows — with less operational drift.';
const DEFAULT_IMAGE = '/og-card.jpg';
const DEFAULT_IMAGE_ALT =
  'Phennx command center: clinical research operations dashboard with studies, visits, and audit-ready workflows';
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;

const normalizeUrl = (pathOrUrl) => {
  if (!pathOrUrl) {
    return SITE_URL;
  }
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl;
  }
  const normalizedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_URL}${normalizedPath}`;
};

export default function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  imageAlt,
  type = 'website',
  schema,
  robots = 'index, follow',
}) {
  const canonicalUrl = normalizeUrl(path);
  const imageUrl = normalizeUrl(image);
  const isDefaultImage = image === DEFAULT_IMAGE;
  const resolvedImageAlt = imageAlt || (isDefaultImage ? DEFAULT_IMAGE_ALT : title);

  return (
    <Helmet>
      <title>{title}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Phennx" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      {isDefaultImage ? (
        <meta property="og:image:width" content={String(DEFAULT_IMAGE_WIDTH)} />
      ) : null}
      {isDefaultImage ? (
        <meta property="og:image:height" content={String(DEFAULT_IMAGE_HEIGHT)} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />

      {schema ? (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      ) : null}
    </Helmet>
  );
}
